/**
 * Parts' charts laid end to end as one performance — what the chart player plays, whether that is
 * one part or every charted part from there to the end of the song.
 *
 * One clip rather than a clip per part played one after another: a stop and a start at every part
 * line is a gap, a second count-in and a voice rebuilt each time. Laid out once, the join is just
 * the next bar.
 */

import { BAND_LAYERS } from './chordBand';
import { hasBandLayer, RHYTHM_PATTERNS } from './chordRhythmPatterns';
import { FIGURES, getViolinBars } from './chordViolin';
import { BASS_INSTRUMENT_ID, buildChordBarsMidi, getChordStrikes } from './chordBarsMidi';
import { getDropBeats, getLayerGain, getPadGain, getPassIntensity, SOFT } from './chartIntensity';
import { getPadSound, tieCommonTones } from './chordPad';
import {
  doubleOctave,
  flattenMidiNotes,
  getPickupBeats,
  rescaleMidiJson,
  shiftMidiJson,
  transposeMidiJson,
  trimLeadInBars,
} from './midiJson';
import { getBeatsPerBar } from './songTiming';

/** Whether a part's MIDI has any notes to play. */
export const hasMelodyNotes = (midiJson) => Boolean(midiJson?.tracks?.some((track) => track.notes?.length > 0));

/**
 * A part's MIDI timed to its chart: transposed, doubled in octaves where set, its empty lead-in bars dropped, re-timed to the
 * chart's tempo, and moved to `atBar` where the clip was played for one instrument of a chart
 * shared between several. So its first bar with notes lands on the chart's first bar, or that one —
 * or its pickup ahead of it, where the clip has one, with times before 0.
 *
 * @param {object} melody - `{ midiJson, instrumentId, semitones, atBar }`, or null
 * @returns {object|null} MIDI JSON, or null when there is nothing to play
 */
export const placeMelody = (melody, { bpm, beatsPerBar }) => {
  if (!bpm || !hasMelodyNotes(melody?.midiJson)) return null;

  const trimmed = trimLeadInBars(doubleOctave(transposeMidiJson(melody.midiJson, melody.semitones || 0)), beatsPerBar);
  const clip = rescaleMidiJson(trimmed, bpm);

  // Placed after the re-timing, so the bars it moves by are the chart's own
  const beats = (melody.atBar || 0) * beatsPerBar - getPickupBeats(melody.midiJson, beatsPerBar);

  return shiftMidiJson(clip, (beats * 60) / bpm);
};

// A note played this far ahead of a pass's first bar line still belongs to that pass
const EARLY_BEATS = 0.125;

// How many of a part's passes its clip was played across: one where it was played once to be
// repeated, every one where the part was played through with its repeats. Never more than the part
// repeats, so a tail run on past a part that doesn't is still one pass.
const getClipPasses = (tune, passDuration, early, repeatCount) => {
  const last = tune[tune.length - 1];

  if (!last) return 1;

  const counted = Math.floor((last.time + early) / passDuration) + 1;

  return Math.max(1, Math.min(counted, Math.round(repeatCount) || 1));
};

// One pass of a clip played across `count`, timed from that pass's start. The first keeps the
// pickup ahead of it and the last whatever runs on past the chart.
const slicePass = (tune, index, count, passDuration, early) => {
  const from = index === 0 ? -Infinity : index * passDuration - early;
  const to = index === count - 1 ? Infinity : (index + 1) * passDuration - early;

  return tune
    .filter((note) => note.time >= from && note.time < to)
    .map((note) => ({ ...note, time: note.time - index * passDuration }));
};

const getNoteEnd = (note) => note.time + Math.max(note.duration, note.sustain || 0);

// Where a strike stops short of the next one — a rest after it — so the views stop lighting it
// there. Everywhere else the next strike takes over, and Infinity leaves that to it.
const getChordEnds = (strikes, secondsPerBeat) =>
  strikes.map(({ time, beats }, i) => {
    const end = time + beats * secondsPerBeat;
    const next = strikes[i + 1]?.time;

    return next !== undefined && end >= next - 1e-6 ? Infinity : end;
  });

// Where each drop silences the band: the beat before its part, on the part ahead's own beat.
// The first segment has nothing ahead of it to drop out of.
const getSilences = (segments, beatsPerBar) =>
  segments.flatMap((segment, index) => {
    const previous = segments[index - 1];

    if (!segment.drop || !previous) return [];

    return [{ from: segment.start - getDropBeats(beatsPerBar) * previous.secondsPerBeat, to: segment.start }];
  });

// Notes with the silences cut out of them: one struck in a silence goes, and one ringing into it
// is let go where it starts
const cutSilences = (notes, silences) =>
  silences.length === 0
    ? notes
    : notes.flatMap((note) => {
        if (silences.some(({ from, to }) => note.time >= from - 1e-6 && note.time < to - 1e-6)) return [];

        const cut = silences.find(({ from }) => note.time < from && getNoteEnd(note) > from);

        if (!cut) return [note];

        const left = cut.from - note.time;
        const sustain = note.sustain ? { sustain: Math.min(note.sustain, left) } : {};

        return [{ ...note, duration: Math.min(note.duration, left), ...sustain }];
      });

// A kept pad's way down is quick, so the verse is in at the line
const PAD_FALL_SECONDS = 0.3;

/**
 * Where the pad's gain changes across a run, for the player to ramp to: up across the last bar of
 * the part before, so it swells with the band into the next, and down quickly at the line. A part
 * the pad comes in at starts there; one it leaves needs nothing, having no pad notes.
 *
 * @returns {Array<{time: number, value: number, ramp: number}>} `ramp` seconds, from `time`
 */
export const getPadLevels = (segments) => {
  const levels = [];

  segments.forEach((segment, index) => {
    const value = segment.padGain || 0;
    const before = segments[index - 1]?.padGain || 0;

    if (value === 0 || value === before) return;
    if (before === 0) {
      levels.push({ time: segment.start, value, ramp: 0 });
    } else if (value > before) {
      const previous = segments[index - 1];
      const ramp = Math.min(previous.secondsPerBar, previous.duration);

      levels.push({ time: segment.start - ramp, value, ramp });
    } else {
      levels.push({ time: segment.start, value, ramp: PAD_FALL_SECONDS });
    }
  });

  return levels;
};

// The chord a part opens on, where it opens on its first downbeat; a drum solo plays none
const getOpeningChord = (entry, timeSignature) => {
  if (!entry || entry.drumsOnly) return null;

  const [first] = getChordStrikes(entry.lines, { bpm: entry.bpm, timeSignature });

  return first && !first.melody && first.time === 0 ? first.chord : null;
};

// A part's chords, each with its own end in seconds, for the violin to ring a landing on into. Each
// part times at its own tempo, so the end is worked out here rather than from the beats.
const getFollowingStrikes = (entry, timeSignature) =>
  !entry || entry.drumsOnly
    ? []
    : getChordStrikes(entry.lines, { bpm: entry.bpm, timeSignature }).map((strike) => ({
        ...strike,
        end: strike.time + (strike.beats * 60) / entry.bpm,
      }));

/**
 * @param {object[]} entries - One per part, in playing order:
 *   `{ lines, bpm, instrumentId, rhythm, melody, repeatCount, countInEntry, intensity, intensityPasses,
 *   build, fill, drop, pad, padMode, layers, drumsOnly }`. A part with no tempo or nothing to play is passed over. `drumsOnly` is a
 *   drum solo: its chart is timed and lit as usual, but only the drums sound. `rhythm`, where given, is
 *   played over the one in options. The intensity fields are chartIntensity.js's; absent is Normal.
 * @param {object} options
 * @param {string} options.timeSignature - The song's; every part shares it
 * @param {string} options.rhythm - Pattern id, or empty for block chords — see buildChordBarsMidi.
 *   Each segment carries the one its part played with.
 * @param {boolean} options.voicing - Voice each chord near the one before it
 * @param {boolean} options.repeats - Play each part repeatCount times rather than once. A part whose
 *   melody plays always plays them all, and a melody played across its repeats plays on through them.
 * @param {boolean} options.melody - Play the parts' MIDI with their charts
 * @param {boolean} options.bass - Bring in the bass player on the styles that have one
 * @param {boolean} options.pad - Bring in the pad on the styles that have one, where the level does
 * @param {string[]} options.band - The band's extra players to bring in the same way, by BAND_LAYERS id
 * @param {string} options.category - The song's, which picks the band under block chords
 * @returns {object|null} `{ midiJson, bpm, duration, segments, melodyNotes, melodyEnd, bassNotes, padNotes, bandNotes,
 *   countInEntry }`,
 *   or null when no part has anything to play.
 *   - `midiJson` is every part's chords as one clip, each note tagged with its part's instrumentId.
 *   - `bpm` is the first part's, which the transport runs at. Times are absolute seconds, so a part
 *     at another tempo is only wider or narrower and the transport never changes tempo mid-run.
 *   - `segments` is one per pass of a part: `{ entryIndex, rhythm, intensity, fill, drop, drums, pass, passes, start, duration, bars,
 *     secondsPerBeat, secondsPerBar, strikes, chordTimes, chordEnds, hasMelody, pickupLead }`,
 *     times inside it counted from its own `start`. `hasMelody` is whether the part's melody plays
 *     with it, and `pickupLead` how many seconds ahead of `start` that melody's pickup comes in.
 *     `drums` is the part's drums switch out of its `layers`, which getRunDrumHits reads.
 *   - `melodyNotes` are tagged the same way, and `melodyEnd` is where the last of them stops. A
 *     pickup on the first part starts before 0: it belongs over the count-in.
 *   - `bassNotes` are the bass player's, tagged with the bass guitar. Kept out of `midiJson` like
 *     the melody, so the keyboard stays the chart's.
 *   - `padNotes` the same, once per instrument its sound layers, a note the next chord shares held on.
 *     `padLevels` is the gain the player ramps the pad to through the run (getPadLevels), each
 *     segment's `padGain` laid end to end.
 *   - `bandNotes` the same, an array per BAND_LAYERS id. `bandLevels` is the gain each ramps to through
 *     the run, out of its notes' `levels`: the violin's swells and fades.
 *   - A part's `drop` silences the piano, bass, pad and band for a beat before its first pass, as the drums
 *     do; the melody plays on, since a pickup into the part may be in it.
 *   - `countInEntry` is the first part's: whether its first bar sounds over the count-in. A later
 *     part's is not played — its opening rest is only silence.
 */
export const buildChartRun = (
  entries,
  {
    timeSignature,
    rhythm = '',
    voicing = false,
    repeats = false,
    melody = true,
    bass = false,
    pad = false,
    band = [],
    category = '',
  } = {},
) => {
  const beatsPerBar = getBeatsPerBar(timeSignature);
  const segments = [];
  // How many violin figures the run has played, so the next part carries the turn on from there
  let violinFigures = 0;
  const notes = [];
  const melodyNotes = [];
  const bassNotes = [];
  const padNotes = [];
  const bandNotes = Object.fromEntries(BAND_LAYERS.map(({ id }) => [id, []]));
  let first = null;
  let countInEntry = false;
  let start = 0;
  let melodyEnd = 0;

  entries.forEach((entry, entryIndex) => {
    // A part played through can be set to a rhythm of its own
    const entryRhythm = entry.rhythm ?? rhythm;
    const options = {
      bpm: entry.bpm,
      timeSignature,
      rhythm: entryRhythm,
      voicing,
      bass,
      pad,
      band,
      layers: entry.layers,
      category,
    };
    // What a pass leads into: the part's own first chord on a repeat, the next part's after the last
    const own = { then: getOpeningChord(entry, timeSignature), following: getFollowingStrikes(entry, timeSignature) };
    const next = {
      then: getOpeningChord(entries[entryIndex + 1], timeSignature),
      following: getFollowingStrikes(entries[entryIndex + 1], timeSignature),
    };
    // The same chart at each level its passes play at, built once a level, pad and chord after it.
    // Every level times alike.
    const clips = new Map();
    const clipAt = (intensity, after, rises = false, variation = 0) => {
      const padHeld = getPadGain(entry, intensity) > 0;
      const key = `${intensity}|${padHeld}|${after === own}|${rises}|${variation}`;

      if (!clips.has(key)) {
        clips.set(key, buildChordBarsMidi(entry.lines, { ...options, ...after, intensity, padHeld, rises, variation }));
      }

      return clips.get(key);
    };
    const clip = clipAt(getPassIntensity(entry, 1), next);

    if (!clip) return;

    if (!first) countInEntry = Boolean(entry.countInEntry);

    first = first || clip;

    const secondsPerBeat = 60 / clip.bpm;
    const secondsPerBar = secondsPerBeat * beatsPerBar;
    const strikes = getChordStrikes(entry.lines, { bpm: clip.bpm, timeSignature });
    const chordTimes = strikes.map(({ time }) => time);
    const chordEnds = getChordEnds(strikes, secondsPerBeat);
    const placed = melody && !entry.drumsOnly ? placeMelody(entry.melody, { bpm: clip.bpm, beatsPerBar }) : null;
    const tune = flattenMidiNotes(placed);
    // Repeats or not, a part with a melody plays every pass: the melody is how it's played
    const passes = repeats || tune.length > 0 ? Math.max(1, Math.round(entry.repeatCount) || 1) : 1;
    const early = EARLY_BEATS * secondsPerBeat;
    const clipPasses = getClipPasses(tune, clip.duration, early, entry.repeatCount);

    for (let pass = 1; pass <= passes; pass += 1) {
      const at = start;
      // A clip played across the repeats plays on from pass to pass rather than starting over on each
      const index = (pass - 1) % clipPasses;
      const slice = slicePass(tune, index, clipPasses, clip.duration, early);
      // A drum solo is never Sparse, which would leave it silent
      const level = getPassIntensity(entry, pass);
      const intensity = entry.drumsOnly ? Math.max(SOFT, level) : level;
      // Whether the pass after this one plays louder, for the violin to swell into
      const nextEntry = entries[entryIndex + 1];
      const louder = pass < passes ? getPassIntensity(entry, pass + 1) : nextEntry && getPassIntensity(nextEntry, 1);
      // The violin's figures carry on from where the last part left them
      const variation = violinFigures % FIGURES.length;
      const played = clipAt(intensity, pass < passes ? own : next, Boolean(louder) && louder > intensity, variation);
      const violinPlays =
        band.includes('violin') &&
        hasBandLayer(
          RHYTHM_PATTERNS.find(({ id }) => id === entryRhythm),
          'violin',
        ) &&
        getLayerGain(entry.layers, 'violin', intensity) > 0;

      if (violinPlays && !entry.drumsOnly) {
        violinFigures += getViolinBars(Math.round(clip.duration / secondsPerBar), intensity).length;
      }

      segments.push({
        entryIndex,
        rhythm: entryRhythm,
        intensity,
        fill: entry.fill !== false,
        drop: pass === 1 && Boolean(entry.drop),
        drumsOnly: Boolean(entry.drumsOnly),
        drums: entry.layers?.drums || null,
        padGain: getPadGain(entry, intensity),
        pass,
        passes,
        start: at,
        duration: clip.duration,
        bars: Math.round(clip.duration / secondsPerBar),
        secondsPerBeat,
        secondsPerBar,
        strikes,
        chordTimes,
        chordEnds,
        hasMelody: slice.length > 0,
        pickupLead: index === 0 && slice.length > 0 ? Math.max(0, -slice[0].time) : 0,
      });

      // A drum solo plays none of the band's notes, only its chart's timing
      if (!entry.drumsOnly) {
        played.tracks[0].notes.forEach((note) =>
          notes.push({ ...note, time: note.time + at, instrumentId: entry.instrumentId }),
        );
        const track = (name) => played.tracks.find((candidate) => candidate.name === name);

        track('Bass')?.notes.forEach((note) =>
          bassNotes.push({ ...note, time: note.time + at, instrumentId: BASS_INSTRUMENT_ID }),
        );

        const padTrack = track('Pad');

        getPadSound(padTrack?.instrument)?.instruments.forEach((instrumentId) =>
          padTrack.notes.forEach((note) => padNotes.push({ ...note, time: note.time + at, instrumentId })),
        );

        BAND_LAYERS.forEach(({ id, name, instrument }) =>
          track(name)?.notes.forEach((note) =>
            bandNotes[id].push({
              ...note,
              time: note.time + at,
              instrumentId: note.instrumentId || instrument,
              ...(note.levels ? { levels: note.levels.map((level) => ({ ...level, time: level.time + at })) } : null),
            }),
          ),
        );
      }
      slice.forEach((note) =>
        melodyNotes.push({ ...note, time: note.time + at, instrumentId: entry.melody.instrumentId }),
      );

      // The clip's own end on its last pass; on another, cut there, its last note's
      if (slice.length > 0) {
        const end =
          index === clipPasses - 1 ? placed.duration - index * clip.duration : Math.max(...slice.map(getNoteEnd));

        melodyEnd = Math.max(melodyEnd, at + end);
      }

      // TODO(play-through): the next part comes in at this chart's last bar line even where its
      // melody runs on past it, and the tail rings over the next part. Check whether it should wait
      // for the melody's last note instead, rounded up to the bar so it still lands on a downbeat.
      start = at + clip.duration;
    }
  });

  if (!first) return null;

  const silences = getSilences(segments, beatsPerBar);
  const bandPlayed = Object.fromEntries(
    Object.entries(bandNotes).map(([id, layerNotes]) => [id, cutSilences(layerNotes, silences)]),
  );

  return {
    midiJson: { ...first, duration: start, tracks: [{ ...first.tracks[0], notes: cutSilences(notes, silences) }] },
    bpm: first.bpm,
    duration: start,
    segments,
    melodyNotes: melodyNotes.sort((a, b) => a.time - b.time),
    melodyEnd,
    bassNotes: cutSilences(bassNotes, silences),
    // Cut before the tie, so a held note doesn't carry on through the drop
    padNotes: tieCommonTones(cutSilences(padNotes, silences)),
    padLevels: getPadLevels(segments),
    bandNotes: bandPlayed,
    // A note a drop takes out takes its gain changes with it
    bandLevels: Object.fromEntries(
      Object.entries(bandPlayed).map(([id, layerNotes]) => [
        id,
        layerNotes.flatMap((note) => note.levels || []).sort((a, b) => a.time - b.time),
      ]),
    ),
    countInEntry,
  };
};

/**
 * When a part is called: on the downbeat of the bar before it comes in, on the part ahead's own bar
 * lines. Where its melody has a pickup, that's the bar before the pickup's, so the call is over
 * before the pickup starts rather than on top of it. The first part is called as playing starts.
 *
 * @param {object[]} segments - A run's, see buildChartRun
 * @param {number} index - The segment being called
 * @returns {number} Seconds into the run
 */
export const getCalloutTime = (segments, index) => {
  const segment = segments[index];
  const previous = segments[index - 1];

  if (!previous) return 0;

  const comesIn = segment.start - (segment.pickupLead || 0);
  const bar = Math.floor((comesIn - previous.start + 1e-6) / previous.secondsPerBar);

  return Math.max(0, previous.start + (bar - 1) * previous.secondsPerBar);
};
